import { Component, ViewChild } from '@angular/core';
import { SelectFilterNewComponent } from '../../dialogs/select-filter-new/select-filter-new.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { filter, map, Observable } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { DiscoveryManager } from '../../../managers/discovery.manager';
import { keys, uniq } from 'lodash-es';
import { takeUntil, tap } from 'rxjs/operators';
import { BaseComponent } from '../../base.component';
import { SearchManager } from '../../../managers/search.manager';
import { MatSort } from '@angular/material/sort';
import { HelpService } from '../../../services/help.service';
import { Filter } from '../../../models/ui/filter.model';

@Component({
    selector: 'lex-review-tracking',
    templateUrl: './review-tracking.component.html',
    styleUrls: ['./review-tracking.component.scss'],
    standalone: false
})
export class ReviewTrackingComponent extends BaseComponent {
	@ViewChild(MatSort, { static: false }) sort: MatSort;

	public dataSource$: Observable<MatTableDataSource<Record<string, string>>>;
	public columnsToDisplay: string[] = [];
	public chartData: ChartData[];
	public xAxisLabel: string = 'Date';
	public yAxisLabel: string = 'Documents';
	public unit: any = 1;
	public dateUnit: any = 1;
	public filter: Filter = null;
	public viewMode: 'graph' | 'table' = 'table';

	constructor(
		private dialog: MatDialog,
		private searchManager: SearchManager,
		private discoveryManager: DiscoveryManager,
		private helpService: HelpService
	) {
		super();
		this.helpService.updateHelpLinkUrl('https://sites.google.com/a/ediscoveryplatform.com/help/home/help/discovery-menu/review-sets');

		this.dataSource$ = this.discoveryManager.report$.pipe(
			filter((reportData) => !!reportData),
			map((reportData) => {
				const source = new MatTableDataSource(reportData);
				setTimeout(() => source.sort = this.sort);
				return source;
			})
		);

		this.discoveryManager.refreshReviewTrackingReport({
			unit: this.unit,
			dateUnit: this.dateUnit,
			filter: this.filter?.savedFilterId
		});

		this.discoveryManager.report$.pipe(
			filter((reportData) => !!reportData),
			tap(() => {
				const dateUnits = ['Documents', 'Pages', 'GB', 'Activity'];
				this.yAxisLabel = dateUnits[this.unit - 1];
			}),
			takeUntil(this.destroy$)
		).subscribe((r) => {
			let columns = r.reduce((p, r) => p.concat(keys(r)), []);
			columns = uniq(columns);
			this.columnsToDisplay = columns;
			const filtered = this.columnsToDisplay.filter((x) => x !== 'date' && x !== 'total');
			this.chartData = filtered.map((x) => {
				return {
					name: x,
					series: r.map((y) => {
						return {
							name: y.date,
							value: y[x]
						};
					})
				};
			});
		});
	}

	public refreshReportData(): void {
		this.discoveryManager.refreshReviewTrackingReport({
			unit: this.unit,
			dateUnit: this.dateUnit,
			filter: this.filter?.savedFilterId
		});
	}

	public displayFilterDialog(): void {
		const dialogConfig = new MatDialogConfig();

		dialogConfig.autoFocus = true;
		dialogConfig.width = '800px';
		dialogConfig.data = {
			filter: this.filter ? JSON.parse(this.filter.filterJson) : null
		};

		const dialogRef = this.dialog.open(SelectFilterNewComponent, dialogConfig);

		dialogRef.afterClosed().subscribe(data => {
			if (!data || data.isCancelled) return;

			if (data.filter) {
				this.searchManager.createFilter({ filters: data.filter }).subscribe((newFilter) => {
					this.filter = newFilter;
					this.discoveryManager.refreshReviewTrackingReport({
						unit: this.unit,
						dateUnit: this.dateUnit,
						filter: this.filter?.savedFilterId
					});
				});
			} else {
				this.discoveryManager.refreshReviewTrackingReport({
					unit: this.unit,
					dateUnit: this.dateUnit,
					filter: this.filter?.savedFilterId
				});
			}
		});
	}
	public exportToExcel(): void {
		this.discoveryManager.exportReviewTrackingReport({
			unit: this.unit,
			dateUnit: this.dateUnit,
			filter: this.filter?.savedFilterId
		}).subscribe((url) => {
			window.open(url, '_blank');
		});
	}

	/**
	 * Patch work until we have time to rewrite / rework how we generate reports.
	 * @param column
	 * @param value
	 */
	public round(column: string, value: any): number | string {
		if(column == 'date') return value;
		return Math.round(value * 100) / 100
	}
}

interface ChartData {
	name: string;
	series: { name: string, value: string }[];
}
